import { inject, Injectable, Type } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { AlertModal } from '@uzurv/rideWss/app/modals/alert/alert.modal';
import { LoadingModalComponent } from '@uzurv/rideWss/app/modals/loading/loading.modal';
import { firstValueFrom, TimeoutError } from 'rxjs';

export interface ModalDismissResult<T = unknown> {
  data?: T;
  role?: string;
}

export interface ModalRef<T = unknown> {
  dialogRef: MatDialogRef<any, T>;
  present(): Promise<void>;
  dismiss(data?: T, role?: string): Promise<void>;
  onDidDismiss(): Promise<ModalDismissResult<T>>;
}

export type ModalOptions<TComponent = unknown, TData = unknown> = MatDialogConfig<TData> & {
  component: Type<TComponent>;
};

export interface ModalDialogOptions {
  isFullScreen?: boolean;
  icon?: string;
  iconColor?: string;
  alignTextCenter?: boolean;
  title?: string;
  titleColor?: string;
  htmlContent?: SafeHtml;
  message?: string;
  buttons?: Array<ModalButton>;
}

export interface ModalButton {
  text: string;
  color?: ThemePalette;
  fill?: string;
  role?: string;
  handler?: (value: any) => boolean | void | { [key: string]: any };
}

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  readonly dialog: MatDialog = inject(MatDialog);
  readonly translateService = inject(TranslateService);

  /**
   * Presents a modal with a specified component and adds accessibility support
   *
   * @param {ModalOptions} opts
   * @returns {Promise<ModalRef>}
   * @memberof ModalService
   */
  async presentComponentModal<TComponent = unknown, TData = unknown, TResult = unknown>(
    opts: ModalOptions<TComponent, TData>
  ): Promise<ModalRef<TResult>> {
    const dialogRef = this.dialog.open<TComponent, TData, TResult>(opts.component, this.mapToDialogConfig(opts));

    const dismissPromise = firstValueFrom(dialogRef.afterClosed()).then(data => ({ data }));

    return {
      dialogRef,
      present: async () => undefined,
      dismiss: async (data?: TResult, _role?: string) => {
        dialogRef.close(data);
        await dismissPromise;
      },
      onDidDismiss: () => dismissPromise
    };
  }

  /**
   * Presents the "Need Help" alert.
   */
  async presentNeedHelpModal(phoneNumber: string): Promise<ModalRef<string>> {
    return this.presentAlertDialog({
      title: this.translateService.instant('needHelpAlert.title'),
      htmlContent: this.translateService.instant('needHelpAlert.text.message', {
        formattedPhoneNumber: phoneNumber
      }),
      buttons: [
        {
          text: this.translateService.instant('button.close'),
          role: 'confirm',
          fill: 'solid',
          color: 'primary'
        }
      ]
    });
  }

  /**
   * Presents an alert-style ModalDialogComponent with height/width based on content.
   * @param modalDialogOptions
   */
  async presentAlertDialog(modalDialogOptions: ModalDialogOptions): Promise<ModalRef<string>> {
    return this.presentComponentModal<AlertModal, ModalDialogOptions, string>({
      component: AlertModal,
      data: modalDialogOptions,
      disableClose: true,
      panelClass: ['modal-dialog', 'auto-height'],
      width: '39rem',
      minHeight: '13rem'
    });
  }

  async presentLoadingDialog(message: string): Promise<ModalRef<void>> {
    return this.presentComponentModal<LoadingModalComponent, { message: string }, void>({
      component: LoadingModalComponent,
      data: { message },
      disableClose: true,
      panelClass: ['loading-modal'],
      restoreFocus: false
    });
  }

  /**
   * Creates a modal based on error type, or a generic error modal if no corresponding type is found.
   * @param err
   */
  async createErrorModalDialog(err: Error): Promise<ModalRef> {
    let message: string = '';
    if (err instanceof TimeoutError) {
      message = this.translateService.instant('error.message.network');
    } else {
      message = err.message;
    }

    return this.presentAlertDialog({
      message: message,
      buttons: [
        {
          text: this.translateService.instant('button.ok'),
          role: 'cancel'
        }
      ]
    });
  }

  /**
   * Errors returned in an effect are not the same error object as the above method.
   * // TODO: We'll need to figure out how to best handle differentiating error types, or if we even want to handle a specific error
   * type as we are above.
   * @param message
   */
  async createNgrxErrorModalDialog(message: string): Promise<ModalRef> {
    return this.presentAlertDialog({
      title: this.translateService.instant('error.dialog.title.generic'),
      message: message,
      buttons: [
        {
          text: this.translateService.instant('button.ok'),
          role: 'cancel'
        }
      ]
    });
  }

  private mapToDialogConfig<TData>(opts: ModalOptions<any, TData>): MatDialogConfig<TData> {
    const { component, panelClass, ...dialogConfig } = opts;

    return {
      ...dialogConfig,
      restoreFocus: dialogConfig.restoreFocus ?? true,
      panelClass: this.parseCssClasses(panelClass)
    };
  }

  private parseCssClasses(cssClass?: string | Array<string>): string | Array<string> | undefined {
    if (Array.isArray(cssClass)) {
      return cssClass;
    }

    if (typeof cssClass === 'string') {
      return cssClass.split(' ').filter(Boolean);
    }

    return undefined;
  }
}
